/** @jsx isolateComponent */
import { useRef, useState } from 'react';
import { css } from '@emotion/core';

import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Animation,
  Colors,
  Icon,
  Link,
  Surface,
  Typography,
} from '@zapier/design-system';
import { SizeType, TrackingFields } from '@zapier/product-get-help/src/types';
import HelpCenterLinkIcon from '@zapier/product-get-help/src/components/shared/HelpCenterLinkIcon';
import { ServiceIcon } from '@zapier/product-get-help/src/components/shared/ServiceIcon';
import { emit } from '@zapier/events-toolkit-browser';
import { USER_INTERACTION_EVENT } from '@zapier/product-get-help/src/constants';

type DocLinkProps = {
  /** Link */
  href: string;
  /** Title */
  title: string;
  /** App ID to render app icon instead of default icon */
  appId?: string;
  /** Size to display. */
  size?: SizeType;
  /** Event tracking properties */
  trackingFields?: TrackingFields;
};

const containerCss = (size: SizeType) => css`
  display: grid;
  grid-column-gap: ${size === 'small' ? '10px' : '20px'};
  grid-template-columns: min-content 1fr min-content;
  align-items: center;
  padding: 0 ${size === 'small' || '10px'};
  height: 50px;
`;

const titleCss = (isActive: boolean) => css`
  ${Typography.smallPrint1Semibold}
  color: ${Colors[isActive ? 'blueJeans' : 'neutral800']};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-decoration: none;
`;

const rightIconCss = (isActive: boolean) => css`
  transition-property: opacity;
  transition-timing-function: ${Animation.transitionTimingFunction};
  transition-duration: ${Animation.transitionDuration};
  opacity: ${isActive ? 1 : 0};
  padding-top: 10px;
  padding-right: 10px;
`;

const helpLinkCss = css`
  text-decoration: none;
`;

/** Renders a single doc for `<DocLinks>`. */
const DocLink = ({ appId, href, size = 'large', title, trackingFields }: DocLinkProps) => {
  const [isActive, setIsActive] = useState(false);

  const onBlur = useRef(() => setIsActive(false)).current;
  const onFocus = useRef(() => setIsActive(true)).current;
  const onMouseEnter = useRef(() => setIsActive(true)).current;
  const onMouseLeave = useRef(() => setIsActive(false)).current;
  let eventAttributes: any;

  if (trackingFields) {
    eventAttributes = {
      system_name: 'support',
      interaction_name: trackingFields.interactionName,
      interaction_goal: trackingFields.interactionGoal,
      event_action: 'click',
      option_selected: href,
      custom_tracking_fields: {
        selection: title,
      },
    };
  }

  return (
    <Link
      css={helpLinkCss}
      href={href}
      onBlur={onBlur}
      onClick={eventAttributes ? () => emit(eventAttributes, USER_INTERACTION_EVENT) : undefined}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      target="_blank"
    >
      <Surface
        backgroundColor="neutral100"
        borderColor="neutral400"
        shadow={isActive ? 'elevation10' : undefined}
      >
        <div css={containerCss(size)}>
          {!appId && (
            <HelpCenterLinkIcon
              isActive={isActive}
              name="fileFilled"
              size={30}
            />
          )}
          {appId && <ServiceIcon alt={title} serviceName={appId} size={30} />}
          <span css={titleCss(isActive)}>{title}</span>
          <span css={rightIconCss(isActive)}>
            <Icon color="blueJeans" name="arrowOffsite" size={18} />
          </span>
        </div>
      </Surface>
    </Link>
  );
};

export default DocLink;
